import { inject, Injectable } from '@angular/core';
import { PerformanceManagementService } from './performance.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    ClearLoading,
    CreateEmployeePerformanceData,
    GetAllPerformance,
    GetAllPerformanceEmployeeByManager,
    GetAllPerformanceManagerByEmployee,
    GetSingleManagerPerformanceByEmployee,
    GetSinglePerformanceByManager,
    GetSinglePerformanceEmployeeByManager,
    SendHrPerformanceData,
    SendManagerPerformanceData,
    UpdateManagerPerformanceData,
    UploadSignature
} from './action';
import { catchError, tap } from 'rxjs';
import { IManagerListRes, IPerformanceDatas, IPerformanceManager } from './interface';

export class IProps {
    performanceDatas!: IPerformanceDatas[];
    performanceManagerHistory!: IPerformanceManager[];
    managerSingleRes!: IManagerListRes | null;
    performanceData!: IManagerListRes | null;
    uploadSignature!: {
        employeeName: string;
        signatureUrl: string;
    } | null;
    loading!: boolean;
    loadingEmployee!: boolean;
    error!: string | null;
}

@State<IProps>({
    name: 'performance_management',
    defaults: {
        performanceDatas: [],
        performanceManagerHistory: [],
        managerSingleRes: null,
        uploadSignature: null,
        performanceData: null,
        loading: false,
        loadingEmployee: false,
        error: null
    }
})
@Injectable()
export class PerformanceManagementStore {
    performanceService: PerformanceManagementService = inject(PerformanceManagementService);

    @Selector()
    static getPerformances(state: IProps) {
        return state.performanceDatas;
    }
    @Selector()
    static getPerformancesManagersHistory(state: IProps) {
        return state.performanceManagerHistory;
    }
    @Selector()
    static getManagerSingleResPerformance(state: IProps) {
        return state.managerSingleRes;
    }
    @Selector()
    static getSignature(state: IProps) {
        return state.uploadSignature;
    }

    @Selector()
    static getPerformance(state: IProps) {
        return state.performanceData;
    }

    @Selector()
    static loading(state: IProps) {
        return state.loading;
    }
    @Selector()
    static loadingEmployee(state: IProps) {
        return state.loadingEmployee;
    }

    @Action(GetAllPerformance)
    getAllPerformance(ctx: StateContext<IProps>, action: GetAllPerformance) {
        ctx.patchState({ loading: true });

        return this.performanceService.getAllPerformanceRequestManager(action.managerId).pipe(
            tap((res) => {
                ctx.patchState({
                    performanceDatas: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false,
                            error: err.message
                        });
                        throw err;
                    });
            })
        );
    }
    @Action(GetSinglePerformanceByManager)
    getAllPerformanceByManager(ctx: StateContext<IProps>, action: GetSinglePerformanceByManager) {
        ctx.patchState({ loading: true });

        return this.performanceService.getPerformanceByManager(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    performanceData: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false,
                            error: err.message
                        });
                        throw err;
                    });
            })
        );
    }
    @Action(GetAllPerformanceManagerByEmployee)
    getAllPerformanceManagerHistry(
        ctx: StateContext<IProps>,
        action: GetAllPerformanceManagerByEmployee
    ) {
        ctx.patchState({ loadingEmployee: true });

        return this.performanceService.getAllPerformanceManagerHistry(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    performanceManagerHistory: res,
                    loadingEmployee: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loadingEmployee: false,
                            error: err.message
                        });
                        if (err.error.statusCode == 500) {
                            ctx.patchState({
                                loadingEmployee: false,
                                error: err.message
                            });
                        }
                        throw err;
                    });
            })
        );
    }

    @Action(ClearLoading)
    clearLoading(ctx: StateContext<IProps>, action: ClearLoading) {
        ctx.patchState({ loadingEmployee: false });
    }

    @Action(GetSingleManagerPerformanceByEmployee)
    getPerformanceManagerSingle(
        ctx: StateContext<IProps>,
        action: GetSingleManagerPerformanceByEmployee
    ) {
        ctx.patchState({ loading: true });

        return this.performanceService.getSingleManagerPerformance(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    managerSingleRes: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false,
                            error: err.message
                        });
                        throw err;
                    });
            })
        );
    }
    @Action(GetAllPerformanceEmployeeByManager)
    getAllPerformanceByEmployee(
        ctx: StateContext<IProps>,
        action: GetAllPerformanceEmployeeByManager
    ) {
        ctx.patchState({ loading: true });

        return this.performanceService.getAllPerformanceEmployeeByManager(action.managerId).pipe(
            tap((res) => {
                ctx.patchState({
                    performanceManagerHistory: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false,
                            error: err.message
                        });
                        throw err;
                    });
            })
        );
    }

    @Action(GetSinglePerformanceEmployeeByManager)
    getSingleEmployeePerformance(
        ctx: StateContext<IProps>,
        action: GetSinglePerformanceEmployeeByManager
    ) {
        ctx.patchState({ loading: true });

        return this.performanceService.getSingleEmployeePerformanceByManager(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    managerSingleRes: res,
                    loading: false
                }),
                    catchError((err) => {
                        ctx.patchState({
                            loading: false,
                            error: err.message
                        });
                        throw err;
                    });
            })
        );
    }

    @Action(SendHrPerformanceData)
    sendHrPerformance(ctx: StateContext<IProps>, action: SendHrPerformanceData) {
        return this.performanceService.sendHrPerformanceData(action.input).pipe(
            tap((res) => {}),
            catchError((err) => {
                ctx.patchState({
                    // loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }
    @Action(SendManagerPerformanceData)
    sendManagerPerformance(ctx: StateContext<IProps>, action: SendManagerPerformanceData) {
        return this.performanceService.sendManagerPerformanceData(action.input).pipe(
            tap((res) => {}),
            catchError((err) => {
                ctx.patchState({
                    // loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }
    @Action(UpdateManagerPerformanceData)
    updateManagerPerformance(ctx: StateContext<IProps>, action: UpdateManagerPerformanceData) {
        const state = ctx.getState();
        return this.performanceService.updateManagerPerformanceData(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    performanceData: {
                        ...state.performanceData,
                        Manager: res.Manager
                    } as IManagerListRes
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    // loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(CreateEmployeePerformanceData)
    sendEmployeePerformance(ctx: StateContext<IProps>, action: CreateEmployeePerformanceData) {
        return this.performanceService.createEmployeePerformanceData(action.input).pipe(
            tap((res) => {}),
            catchError((err) => {
                ctx.patchState({
                    // loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(UploadSignature)
    uploadSignature(ctx: StateContext<IProps>, action: UploadSignature) {
        return this.performanceService.uploadSignature(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    uploadSignature: res
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    // loading: false,
                    error: err.message
                });
                throw err;
            })
        );
    }
}
